// MedicineFinder.jsx
import React, { useState } from "react";
import { useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import MediNewsletterSignup from "./MediNewsletterSignup";
import MediBusinessProfileForm from './MediBusinessProfileForm'; 
import { logout } from '../../redux/actions/authActions';

const MedicineFinder = () => {
  const authState = useSelector(state => state.authReducer);
  const { isLoggedIn } = authState;
  const [userName, setUserName] = useState(authState.user.name);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  return (
    <div className="font-sans text-white bg-black">
      <header className="flex justify-between items-center px-6 py-4 bg-black shadow-md sticky top-0 z-50 text-white">
        <div className="text-lg lg:text-xl font-extrabold tracking-wide text-yellow-200">💊 Medicine Finder</div>
        <div className="hidden md:flex items-center space-x-4">
          {isLoggedIn ? (
            <>
              <span className="font-medium text-xs text-yellow-200">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-9 h-9 flex items-center justify-center text-lg bg-yellow-200 text-green-900 rounded-full hover:bg-yellow-500 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="px-4 py-2 text-sm border border-yellow-200 text-yellow-200 rounded-full hover:bg-yellow-200 hover:text-green-900 transition"
              >
                Login
              </button>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <div className="md:hidden flex items-center justify-center w-24">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-lg lg:text-xl text-yellow-200 focus:outline-none"
            aria-label="Toggle Menu"
          >
            {isMobileMenuOpen ? "×" : "☰"}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-black px-6 py-4 space-y-1 text-center text-white">
          {isLoggedIn && (
            <div className="flex flex-col items-center justify-center text-center">
              <p className="text-white font-medium text-xs">{userName}</p>
              <div className="flex flex-row space-x-4 mt-2">
                <button
                  onClick={handleLogout}
                  className="w-10 h-10 flex items-center justify-center text-lg bg-yellow-200 text-green-900 rounded-full hover:bg-yellow-500 transition"
                >
                  ⏻
                </button>
              </div>
            </div>
          )}
          {!isLoggedIn && (
            <div className="flex justify-center space-x-4 pt-2">
              <button className="px-4 py-2 text-sm border border-yellow-200 text-yellow-200 rounded-full hover:bg-yellow-200 hover:text-green-900 transition">Login</button>
            </div>
          )}
        </div>
      )}

      <nav className="border-t border-b border-blue-400 font-medium flex gap-4 px-4 py-4">
        <a href="#deals" className="hover:text-yellow-200 transition">New Launches</a>
        <a href="#whyus" className="hover:text-yellow-200 transition">Why Us</a>
        <a href="#business" className="hover:text-yellow-200 transition">For Businesses</a>
        <a href="#signup" className="hover:text-yellow-200 transition">Newsletter</a>
      </nav>
      <section
        className="relative h-[75vh] sm:h-[85vh] bg-cover bg-center flex items-center justify-center text-white"
        style={{ backgroundImage: "url('/assets/banners/medifinder.jpg')" }}
      >
        <div className="absolute inset-0 bg-green-900 bg-opacity-60"></div>
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-2xl">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight text-yellow-200">Discover Your Medicines</h1>
          <p className="text-lg font-light">Find It Free. Check Nearby.</p>
          <button 
            onClick={() => navigate('/medi-catalogue')}
            className="mt-6 px-8 py-3 font-semibold bg-yellow-200 text-green-900 rounded-full hover:bg-yellow-500 transition-all"
          >
            Explore Medicines
          </button>
        </div>
      </section>

      <section id="medi" className="py-4 px-4 bg-black border border-green-400">
        <h2 className="text-lg lg:text-xl font-bold text-center mb-4 text-yellow-200">Top Medicines</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {[{
              id: 1, title: "Paracetamol 500mg", description: "Cipla Ltd, Mumbai | Common pain reliever and fever reducer. Fast-acting and widely used in mild to moderate pain cases."
            }, {
              id: 2, title: "Nux Vomica 30C", description: "SBL Pvt Ltd, New Delhi | Homeopathic remedy commonly used for indigestion, nausea, and irritability. Derived from the poison nut tree."
            }, {
              id: 3, title: "Ashwagandha Capsules", description: "Patanjali Ayurved Ltd, Haridwar | Ayurvedic adaptogen known to reduce stress, improve stamina and support overall vitality."
            }
          ].map((medi) => (
            <div key={medi.id} className="bg-black border border-yellow-100 shadow-md hover:shadow-xl transition rounded-xl overflow-hidden">
              <img 
                src={`/api/downloads/thinkmedicine/medicines/${medi.id}.png`} 
                alt={"Medicine Image " + medi.id} 
                className="w-full h-52 object-cover"
              />
              <div className="p-5">
                <h3 className="text-md lg:text-lg font-semibold text-white">{medi.title}</h3>
                <p className="text-white mt-2 text-sm lg:text-md ">{medi.description}</p>
                <button 
                  onClick={() => navigate(`/medi/${medi.id}`)}
                  className="mt-4 px-5 py-2 bg-yellow-200 text-green-900 rounded-md hover:bg-yellow-500 text-sm transition">
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="whyus" className="py-4 px-4 sm:px-6 bg-black border border-green-600 text-white">
        <h2 className="text-lg lg:text-xl font-bold text-center mb-4 text-yellow-200">Why MediFinder?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 text-center">
          {[{
            title: "Best Prices", emoji: "💰", desc: "We negotiate directly with partners to offer unbeatable prices."
          }, {
            title: "Verified Partners", emoji: "✅", desc: "Only trusted, high-quality resorts and hotels make the list."
          }, {
            title: "24/7 Support", emoji: "📞", desc: "Our team is always ready to assist you with your booking journey."
          }].map((feature, idx) => (
            <div key={idx}>
              <div className="text-2xl lg:text-3xl mb-2">{feature.emoji}</div>
              <h4 className="text-lg lg:text-xl font-semibold mb-2 text-yellow-200">{feature.title}</h4>
              <p className="text-white text-sm lg:text-md">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Business Join Section */}
      <MediBusinessProfileForm />

      {/* Newsletter Signup */}
      <MediNewsletterSignup />

      <footer className="bg-black text-white py-2 px-4">
        <div className="flex flex-col lg:flex-row justify-between items-center text-center lg:text-left">
          <p className="text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions.</p>
          <div className="flex space-x-6 mt-4 md:mt-0 text-sm text-white">
            <Link to="/about-us" className="text-white hover:text-yellow-200 transition">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-yellow-200 transition">Privacy</Link>
            <Link to="/terms-and-conditions" className="text-white hover:text-yellow-200 transition">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MedicineFinder;
